/* Итог заказа в рамке хвоста шаблона (engine-kit, план M3 §2 S4, D5): проигрывание и «Скачать».
   Правила:
   - файл итога — только авторизованная выгрузка сервиса заказа (svc.actions.openResult: тот же срок, занятость и текст ошибки, что у родных
     хвостов), один раз на владельца, id:revision:index и только по нажатию — без автозагрузки, как родные кружок и ролик. Сервис держит
     один результат, поэтому файл каждого индекса копируется в память итога: у каждого индекса свой Blob и свой адрес; выгрузки идут
     по одной (сервис не берёт вторую, пока идёт первая);
   - настоящий <video playsinline> встаёт внутрь рамки шаблона — .ar-pic i кружка (BotRun hero shape circle, BC.rv.ring), .pv рамки ролика
     (.pr-vf BotRun.video и BC.rv.video, .mc-vf карточки товара, .pr-vf в .rl-v рилса) — поверх картинки, не заменяя разметку рамки;
     виден с первого кадра (до него прозрачен);
   - ход (timeupdate) ведёт полосу шаблона: у кружка — .ar-ring .pl, пилюля .ar-pill, кнопка в .ar-mid и классы play/paused блока .ar
     (как playPaint BotRun); у ролика — класс on, .pb, .vp i (scaleX, без анимации CSS) и время .vt (как BC.rv у настоящего файла);
     звук — Audio без разметки: ход рисует paint экрана (волна и время);
   - download() — файл для BC.saveBtn; peaks() — волна и длина из файла (filePeaks);
   - stop() — пауза на месте или к началу (уход, закрытие, смена языка); release() — стоп, узлы <video> уходят, адреса отзываются
     (смена кадра); другой заказ, ревизия или владелец — прежние файлы отзываются сами при следующем обращении. */
import type { SpeechOrder } from '../../../api/speech';
import { uiText } from '../../../i18n';
import { WAVE_N, filePeaks } from '../../voice/voice-model';
import { safeMediaUrl } from '../studio-content';
import type { OrderService } from './order-service';

/** Как рисуется ход: кольцо кружка, рамка ролика или звук без разметки (ход рисует экран) */
export type ResultKind = 'circle' | 'frame' | 'audio';
/** Рамки итога шаблона: кнопка рамки (её отдают в play/toggle) и место для <video> внутри неё */
export const RESULT_FRAMES = {
  /** BotRun hero shape circle и BC.rv.ring: кнопка .ar-c, картинка в .ar-pic > i */
  circle: { button: '.ar-c', slot: '.ar-pic i', kind: 'circle' },
  /** BotRun.video (TAIL.video) и BC.rv.video: кнопка .pr-vf, обложка .pv */
  frame: { button: '.pr-vf', slot: '.pv', kind: 'frame' },
  /** Видео товара карточки маркетплейса */
  mcard: { button: '.mc-vf', slot: '.pv', kind: 'frame' },
  /** Варианты рилса: BC.rv.video в карточке .rl-v */
  reels: { button: '.rl-v .pr-vf', slot: '.pv', kind: 'frame' },
} as const satisfies Record<string, { button: string; slot: string; kind: ResultKind }>;

export interface ResultFile {
  index: number;
  url: string;
  blob: Blob;
  mime: string;
  name: string;
}
export interface ResultMediaOptions {
  svc: Pick<OrderService, 'get' | 'actions'>;
  /** Значок шаблона (ic движка) */
  ic(name: string): string;
}
export interface PlayOptions {
  /** Кнопка рамки: .ar-c у кружка, .pr-vf / .mc-vf у ролика; у звука — не нужна */
  el?: HTMLElement | null;
  kind: ResultKind;
  /** Длительность до того, как файл её узнал, с (снимок заказа) */
  dur?: number | null;
  /** Доля длительности, с которой начать (нажатие на волну) */
  at?: number;
  /** Обложка ролика (poster_url итога; только безопасный адрес) */
  poster?: unknown;
  /** Свой показ хода поверх рамки (звук — волна и время экрана): позиция и длительность, с; идёт ли */
  paint?(pos: number, dur: number, on: boolean): void;
  /** Дослушали или досмотрели: позиция уже в начале */
  onEnd?(): void;
}
export interface ResultMedia {
  /** id:revision:index для индекса готового заказа сервиса; '' — заказ не готов или такого файла нет */
  key(index: number): string;
  /** Уже загруженный файл индекса (того же владельца, заказа и ревизии) */
  file(index: number): ResultFile | null;
  /** Файл индекса: один раз на ключ (повторный вызов — тот же промис). null — не вышло (ошибка — svc.get().error) или заказ уже другой */
  open(index: number): Promise<ResultFile | null>;
  /** Нажатие на итог: тот же индекс и рамка — пауза или продолжение; другой — прежний стоп, файл (open), <video> в рамке и пуск.
      false — проигрывать нечего (файла нет, рамки нет, нажатие устарело) */
  toggle(index: number, o: PlayOptions): Promise<boolean>;
  /** Индекс, который сейчас звучит или показывается; null — ничего */
  playing(): number | null;
  /** Стоп: rewind (по умолчанию) — к началу и забыть; иначе пауза на месте */
  stop(rewind?: boolean): void;
  /** Файл для «Скачать» (BC.saveBtn): бросает, если файла нет */
  download(index: number): Promise<{ data: Blob; name: string }>;
  /** Волна и длина звука из файла индекса; null — не вышло */
  peaks(index: number, n?: number): Promise<{ peaks: number[]; durationMs: number } | null>;
  /** Итог больше не нужен (смена кадра, закрытие): стоп, <video> уходят, адреса отзываются, идущие выгрузки ничего не положат */
  release(): void;
}

interface Playing {
  index: number;
  kind: ResultKind;
  el: HTMLElement | null;
  media: HTMLMediaElement;
  o: PlayOptions;
  /** aria-label рамки до пуска */
  label: string | null;
  on: boolean;
  /** Каким нарисован значок кнопки (null — ещё не рисовался) */
  shown: boolean | null;
}

/** BotCore.mmss: 0:08 (floor — прошедшее время плеера) */
function mmss(seconds: number, floor = false) {
  const s = Math.max(0, (floor ? Math.floor : Math.round)(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
const MEDIA_ATTR = 'data-ek-media';

export function createResultMedia(opts: ResultMediaOptions): ResultMedia {
  const { svc } = opts;
  /** Владелец, заказ и ревизия, к которым относятся файлы в памяти */
  let scope = '';
  const files = new Map<number, ResultFile>();
  const flights = new Map<number, Promise<ResultFile | null>>();
  const attached = new Set<HTMLVideoElement>();
  let queue: Promise<unknown> = Promise.resolve();
  /** Поколение памяти: release() и смена заказа делают идущие выгрузки ненужными */
  let gen = 0;
  /** Номер нажатия: пока грузится файл, новое нажатие отменяет прежнее */
  let seq = 0;
  let cur: Playing | null = null;

  const doneOrder = (order: SpeechOrder | null | undefined) => (order?.status === 'done' && order.result?.available ? order : null);
  const assetOf = (order: SpeechOrder | null | undefined, index: number) => doneOrder(order)?.result?.assets.find(asset => asset.index === index);
  function scopeOf(s = svc.get()): string {
    const order = doneOrder(s.order);
    return order ? `${s.identity?.owner ?? ''}|${order.id}|${order.result_revision}` : '';
  }
  function forget() {
    gen++;
    flights.clear();
    files.forEach(item => URL.revokeObjectURL(item.url));
    files.clear();
  }
  /** Память — для текущего владельца, заказа и ревизии; сменились — прежние файлы отзываются */
  function fit(): string {
    const at = scopeOf();
    if (at !== scope) {
      if (cur) stop(true);
      forget();
      scope = at;
    }
    return at;
  }

  async function fetchFile(index: number, at: string, g: number): Promise<ResultFile | null> {
    if (g !== gen || scopeOf() !== at) return null;
    const before = svc.get().result;
    await svc.actions.openResult(index);
    const now = svc.get();
    const result = now.result;
    if (g !== gen || scopeOf(now) !== at || !result || result === before) return null;
    const blob = await (await fetch(result.url)).blob();
    if (g !== gen || scopeOf() !== at) return null;
    const item: ResultFile = { index, url: URL.createObjectURL(blob), blob, mime: result.mime, name: result.name };
    files.set(index, item);
    return item;
  }
  function open(index: number): Promise<ResultFile | null> {
    const at = fit();
    if (!at || !assetOf(svc.get().order, index)) return Promise.resolve(null);
    const hit = files.get(index);
    if (hit) return Promise.resolve(hit);
    const going = flights.get(index);
    if (going) return going;
    const g = gen;
    const run = queue.then(() => fetchFile(index, at, g)).catch(() => null);
    queue = run;
    flights.set(index, run);
    void run.finally(() => {
      if (flights.get(index) === run) flights.delete(index);
    });
    return run;
  }

  /* ---- рамка и ход ---- */
  function attach(el: HTMLElement | null, kind: ResultKind, url: string, poster: unknown): HTMLMediaElement | null {
    if (kind === 'audio') return new Audio(url);
    const slot = el?.querySelector<HTMLElement>(kind === 'circle' ? RESULT_FRAMES.circle.slot : RESULT_FRAMES.frame.slot);
    if (!slot) return null;
    let video = slot.querySelector<HTMLVideoElement>(`video[${MEDIA_ATTR}]`);
    if (!video) {
      video = document.createElement('video');
      video.setAttribute(MEDIA_ATTR, '');
      video.setAttribute('playsinline', '');
      video.playsInline = true;
      video.preload = 'auto';
      /* до первого кадра прозрачен: под ним картинка рамки */
      video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0';
      const shown = video;
      shown.addEventListener('loadeddata', () => {
        shown.style.opacity = '1';
      });
      slot.appendChild(video);
      attached.add(video);
    }
    const cover = safeMediaUrl(poster);
    if (cover && video.getAttribute('poster') !== cover) video.setAttribute('poster', cover);
    if (video.getAttribute('src') !== url) video.setAttribute('src', url);
    /* у рамки ролика движение обложки и полоса — анимации CSS шаблона (.on): у настоящего файла их нет, ход ведёт сам файл */
    if (kind === 'frame') slot.style.animation = 'none';
    return video;
  }
  const durOf = (p: Playing) => {
    const d = p.media.duration;
    return Number.isFinite(d) && d > 0 ? d : p.o.dur && p.o.dur > 0 ? p.o.dur : 1;
  };
  function label(p: Playing, on: boolean) {
    if (!p.el) return;
    if (on) p.el.setAttribute('aria-label', uiText('Пауза'));
    else if (p.label !== null) p.el.setAttribute('aria-label', p.label);
    else p.el.removeAttribute('aria-label');
  }
  function paintCircle(p: Playing, pos: number, d: number, on: boolean) {
    const el = p.el!;
    const box = el.closest('.ar') ?? el;
    box.classList.toggle('play', on);
    box.classList.toggle('paused', !on && pos > 0);
    const ring = el.querySelector<SVGElement>('.ar-ring .pl');
    if (ring) ring.style.strokeDashoffset = String(100 - Math.min(1, pos / d) * 100);
    const pill = el.querySelector('.ar-pill');
    if (pill) pill.textContent = on || pos > 0 ? `${mmss(pos, true)} / ${mmss(d)}` : mmss(d);
    if (p.shown === on) return;
    p.shown = on;
    const mid = el.querySelector('.ar-mid');
    if (mid) mid.innerHTML = `<span class="pb${on ? ' on' : ''}">${opts.ic(on ? 'pause' : 'play')}</span>`;
    label(p, on);
  }
  function paintFrame(p: Playing, pos: number, d: number, on: boolean) {
    const el = p.el!;
    el.classList.toggle('on', on);
    const bar = el.querySelector<HTMLElement>('.vp i');
    if (bar) {
      bar.style.animation = 'none';
      bar.style.transform = `scaleX(${Math.min(1, pos / d).toFixed(4)})`;
    }
    const time = el.querySelector('.vt');
    if (time) time.textContent = on || pos > 0 ? `${mmss(pos, true)} / ${mmss(d)}` : mmss(d);
    if (p.shown === on) return;
    p.shown = on;
    const pb = el.querySelector('.pb');
    if (pb) pb.innerHTML = opts.ic(on ? 'pause' : 'play');
    label(p, on);
  }
  function paint(p: Playing) {
    const pos = p.media.currentTime || 0,
      d = durOf(p);
    p.o.paint?.(pos, d, p.on);
    if (!p.el?.isConnected) return;
    if (p.kind === 'circle') paintCircle(p, pos, d, p.on);
    else if (p.kind === 'frame') paintFrame(p, pos, d, p.on);
  }
  function seek(p: Playing, sec: number) {
    try {
      p.media.currentTime = sec;
    } catch {
      /* файл ещё не готов к перемотке: начнётся с начала */
    }
  }
  function resume(p: Playing, at?: number) {
    const d = durOf(p);
    if (at !== undefined && Number.isFinite(at)) seek(p, Math.max(0, Math.min(1, at)) * d);
    else if (p.media.ended || p.media.currentTime >= d) seek(p, 0);
    p.on = true;
    paint(p);
    let started: Promise<void> | undefined;
    try {
      started = p.media.play();
    } catch {
      started = Promise.reject(new Error('play_failed'));
    }
    /* браузер не пустил (автозапуск, битый файл) — кнопка не залипает на паузе */
    started?.catch(() => {
      if (cur !== p) return;
      p.on = false;
      paint(p);
    });
  }
  function begin(index: number, o: PlayOptions, media: HTMLMediaElement) {
    const el = o.el ?? null;
    const p: Playing = { index, kind: o.kind, el, media, o, label: el?.getAttribute('aria-label') ?? null, on: false, shown: null };
    cur = p;
    const live = () => cur === p;
    media.addEventListener('timeupdate', () => {
      if (live()) paint(p);
    });
    media.addEventListener('loadedmetadata', () => {
      if (live()) paint(p);
    });
    media.addEventListener('ended', () => {
      if (!live()) return;
      p.on = false;
      seek(p, 0);
      paint(p);
      cur = null;
      p.o.onEnd?.();
    });
    resume(p, o.at);
  }
  async function toggle(index: number, o: PlayOptions): Promise<boolean> {
    const el = o.el ?? null;
    const p = cur;
    if (p && p.index === index && p.kind === o.kind && p.el === el && (!el || el.isConnected) && scopeOf() === scope) {
      if (p.on) {
        p.on = false;
        p.media.pause();
        paint(p);
      } else resume(p, o.at);
      return true;
    }
    if (cur) stop(true);
    const ticket = ++seq;
    const item = await open(index);
    if (!item || ticket !== seq || (el && !el.isConnected)) return false;
    const media = attach(el, o.kind, item.url, o.poster);
    if (!media) return false;
    begin(index, o, media);
    return true;
  }
  function stop(rewind = true) {
    const p = cur;
    if (!p) return;
    p.on = false;
    p.media.pause();
    if (rewind) seek(p, 0);
    paint(p);
    if (rewind) cur = null;
  }

  return {
    key(index) {
      const order = svc.get().order;
      return assetOf(order, index) ? `${order!.id}:${order!.result_revision}:${index}` : '';
    },
    file(index) {
      return fit() ? (files.get(index) ?? null) : null;
    },
    open,
    toggle,
    playing: () => (cur?.on ? cur.index : null),
    stop,
    async download(index) {
      const item = await open(index);
      if (!item) throw new Error('result_unavailable');
      return { data: item.blob, name: item.name };
    },
    async peaks(index, n = WAVE_N) {
      const item = await open(index);
      return item ? filePeaks(item.blob, n) : null;
    },
    release() {
      seq++;
      stop(true);
      for (const video of attached) {
        video.pause();
        video.removeAttribute('src');
        video.remove();
      }
      attached.clear();
      forget();
      scope = '';
    },
  };
}
